import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { AlertCircle, CheckCircle2, HelpCircle } from 'lucide-react';
import {
    analyticsCompactBadgeSx,
    analyticsPanelSx,
    analyticsSecondaryControlSx,
} from '../../styles/analyticsMsiStyles';
import {
    securityCountry,
    securityIdentityPresentation,
} from '../../utils/securityIdentityPresentation';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import { posSizingActionButtonSx } from './tableStyles';
import { formatValue } from './positionSizingFormat';
import DataTable from '../data_table/DataTable';
import KitTooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';

function formatTradeIdea(value) {
    if (value == null || String(value).trim() === '') return '—';
    const normalized = Number(value);
    return Number.isFinite(normalized) ? formatValue(normalized) : '—';
}

function formatImportGmv(value) {
    if (value == null || String(value).trim() === '') return '—';
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) return '—';
    const text = formatValue(normalized, { field: 'gmv' });
    return text.startsWith('-') ? `-$${text.slice(1)}` : `$${text}`;
}

const getRowKey = (row) => row.key;

/**
 * One of the dialog's security lists (importing, removing, draft-only):
 * identity (the field picked in its header, shared by the lists), country,
 * trade idea and the GMV in `gmvField`, in the order given.
 */
function SecurityListTable({
    rows,
    gmvField,
    ariaLabel,
    identityDisplayField,
    onIdentityDisplayFieldChange,
}) {
    const columns = [
        {
            id: 'identity',
            header: securityIdentityFieldLabel(identityDisplayField),
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const identity = securityIdentityPresentation(original, identityDisplayField);
                return (
                    <KitTooltip content={identity.tooltip || identity.value}>
                        <span className="block truncate">{identity.value}</span>
                    </KitTooltip>
                );
            },
            meta: {
                width: '44%',
                headerControls: () => (
                    <SecurityIdentityFieldMenu
                        value={identityDisplayField}
                        onChange={onIdentityDisplayFieldChange}
                    />
                ),
            },
        },
        {
            id: 'country',
            header: 'Country',
            accessorFn: (row) => securityCountry(row),
            enableSorting: false,
            meta: { width: '16%' },
        },
        {
            id: 'trade_idea',
            header: 'Trade Idea',
            enableSorting: false,
            cell: ({ row: { original } }) => formatTradeIdea(original.trade_idea),
            meta: { width: '20%', align: 'right' },
        },
        {
            id: 'gmv',
            header: 'GMV',
            enableSorting: false,
            cell: ({ row: { original } }) => formatImportGmv(original[gmvField]),
            meta: { width: '20%', align: 'right' },
        },
    ];
    return (
        <DataTable
            aria-label={ariaLabel}
            data={rows}
            columns={columns}
            getRowId={getRowKey}
            maxHeight={260}
            rowHeight={32}
            headerHeight={34}
        />
    );
}

SecurityListTable.propTypes = {
    rows: PropTypes.array.isRequired,
    gmvField: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    identityDisplayField: PropTypes.string.isRequired,
    onIdentityDisplayFieldChange: PropTypes.func.isRequired,
};

// A setting whose saved value no longer matches is tinted in its value cells.
const STALE_VALUE_CELL_CLASS_NAME = 'bg-ui-warning-bg';
const CONTEXT_STATUS = Object.freeze({
    current: {
        Icon: CheckCircle2,
        label: 'Current',
        tooltip: 'Matches current inputs',
        className: 'text-ui-success',
    },
    stale: {
        Icon: AlertCircle,
        label: 'Stale',
        tooltip: 'Changed since saved',
        className: 'text-ui-warning',
    },
    unknown: {
        Icon: HelpCircle,
        label: 'Unknown',
        tooltip: 'Comparison unavailable',
        className: 'text-content-secondary',
    },
});

function contextStatus(matches) {
    if (matches === true) return CONTEXT_STATUS.current;
    if (matches === false) return CONTEXT_STATUS.stale;
    return CONTEXT_STATUS.unknown;
}

/**
 * The saved-versus-current settings: Setting, Current (the saved value),
 * Incoming (the current value; only with `showIncoming`) and a status icon.
 * While `loading`, every status is unknown.
 */
function ContextComparisonTable({ rows, showIncoming, loading }) {
    const matchesOf = (row) => (loading ? null : row.matches);
    const staleValueClassName = ({ row }) => (
        matchesOf(row.original) === false ? STALE_VALUE_CELL_CLASS_NAME : undefined
    );
    const columns = [
        {
            id: 'label',
            header: 'Setting',
            enableSorting: false,
            cell: ({ row: { original } }) => <span className="font-semibold">{original.label}</span>,
        },
        {
            id: 'savedValue',
            header: 'Current',
            accessorFn: (row) => row.savedValue,
            enableSorting: false,
            meta: { cellClassName: staleValueClassName },
        },
        ...(showIncoming ? [{
            id: 'currentValue',
            header: 'Incoming',
            accessorFn: (row) => row.currentValue,
            enableSorting: false,
            meta: { cellClassName: staleValueClassName },
        }] : []),
        {
            id: 'status',
            header: 'Status',
            size: 56,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const status = contextStatus(matchesOf(original));
                return (
                    <KitTooltip content={status.tooltip}>
                        <status.Icon
                            role="img"
                            aria-label={status.label}
                            aria-hidden={false}
                            size={18}
                            strokeWidth={2.25}
                            className={cx('inline-block align-middle', status.className)}
                        />
                    </KitTooltip>
                );
            },
            meta: { align: 'center', wrap: true },
        },
    ];
    return (
        <DataTable
            aria-label="Position Sizing context comparison"
            data={rows}
            columns={columns}
            getRowId={getRowKey}
            tableLayout="auto"
            rowHeight={32}
            headerHeight={34}
        />
    );
}

ContextComparisonTable.propTypes = {
    rows: PropTypes.array.isRequired,
    showIncoming: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
};

function DraftFreshnessHeaderControl({
    summary,
    onSyncClick,
    syncDisabled = false,
    syncInProgress = false,
    detailsOpen,
    onDetailsOpenChange,
    triggerVariant = 'status',
    headerControl = null,
    presentation = 'draft',
}) {
    const [internalDetailsOpen, setInternalDetailsOpen] = useState(false);
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    const [syncError, setSyncError] = useState(null);
    const lastResolvedSummaryRef = useRef(null);

    const isDetailsControlled = typeof onDetailsOpenChange === 'function';
    const resolvedDetailsOpen = isDetailsControlled ? Boolean(detailsOpen) : internalDetailsOpen;
    const hasVisibleSummary = Boolean(summary?.visible);
    const showSyncAction = Boolean(summary?.syncActionVisible ?? summary?.isStale);
    const isSyncActionDisabled = syncDisabled || summary?.loading || !showSyncAction;
    const optimizerPresentation = presentation === 'optimizer';
    if (hasVisibleSummary && !summary?.loading) {
        lastResolvedSummaryRef.current = summary;
    }
    const dialogSummary = (
        summary?.loading
        && lastResolvedSummaryRef.current
    )
        ? lastResolvedSummaryRef.current
        : summary;
    const dialogHasVisibleSummary = Boolean(dialogSummary?.visible);
    const dialogShowSyncAction = Boolean(
        dialogSummary?.syncActionVisible ?? dialogSummary?.isStale
    );
    const dialogImportRows = dialogSummary?.importRows || [];
    const dialogRemovalRows = dialogSummary?.removalRows || [];
    const dialogDraftOnlyRows = dialogSummary?.draftOnlyRows || [];
    const contextComparisonRows = dialogSummary?.contextComparisonRows || [];
    const showIncomingContext = dialogSummary?.isStale === true
        || contextComparisonRows.some(row => row.matches === false)
        || dialogImportRows.length > 0 || dialogRemovalRows.length > 0;
    const showDetailText = (
        dialogSummary?.isStale
        || dialogSummary?.contextState === 'unavailable'
        || dialogImportRows.length > 0
        || dialogRemovalRows.length > 0
        || dialogDraftOnlyRows.length > 0
        || contextComparisonRows.length === 0
    );
    const statusLabel = optimizerPresentation ? 'Pos Sizing Context' : 'Draft Freshness';
    const dialogTitle = optimizerPresentation ? 'Position Sizing Context' : 'Draft Freshness';
    const detailsTooltip = useMemo(() => {
        if (syncInProgress) return 'Syncing draft';
        if (summary?.loading) return 'Checking freshness';
        if (optimizerPresentation) {
            return showSyncAction
                ? 'View the saved Position Sizing context and Sync Draft'
                : 'View the saved Position Sizing context';
        }
        return showSyncAction ? 'View draft freshness and sync' : 'View draft freshness';
    }, [optimizerPresentation, showSyncAction, summary?.loading, syncInProgress]);

    if (!hasVisibleSummary && triggerVariant !== 'imports') {
        return null;
    }

    const setDetailsOpen = (nextOpen) => {
        if (isDetailsControlled) {
            onDetailsOpenChange(nextOpen);
            return;
        }
        setInternalDetailsOpen(nextOpen);
    };

    const handleOpenDetails = () => {
        setSyncError(null);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setSyncError(null);
        setDetailsOpen(false);
    };

    const handleSync = async () => {
        setSyncError(null);
        try {
            const result = await onSyncClick?.();
            if (result?.success === true) {
                handleCloseDetails();
            } else {
                setSyncError(result?.message || 'Position Sizing could not sync. Please retry.');
            }
        } catch (error) {
            console.error('Error syncing draft freshness:', error);
            setSyncError('Position Sizing could not sync. Please retry.');
        }
    };

    return (
        <>
            {triggerVariant === 'dialog-only' ? null : triggerVariant === 'imports' ? (
                <Tooltip title="Position sizing imports and draft freshness">
                    <IconButton
                        size="small"
                        onClick={handleOpenDetails}
                        aria-label="Open position sizing imports"
                        sx={posSizingActionButtonSx}
                    >
                        <CloudUploadOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : (
                <Box
                    sx={[{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                        px: 0.75,
                        py: 0.5,
                        borderRadius: 2,
                        border: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        backgroundColor: theme => alpha(theme.palette.background.paper, 0.92),
                        minWidth: 0,
                    }, analyticsPanelSx]}
                >
                    <Chip
                        size="small"
                        variant="outlined"
                        label={statusLabel}
                        sx={[{ maxWidth: '100%' }, analyticsCompactBadgeSx]}
                    />
                    <Chip
                        size="small"
                        color={summary.loading ? 'info' : summary.statusColor}
                        label={summary.loading ? 'Checking...' : summary.freshnessLabel}
                    />
                    <Tooltip title={detailsTooltip}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleOpenDetails}
                                disabled={syncDisabled}
                                aria-label={optimizerPresentation
                                    ? 'View saved Position Sizing context details'
                                    : 'View draft freshness details'}
                                sx={[{
                                    borderRadius: 1.5,
                                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                }, analyticsSecondaryControlSx]}
                            >
                                {syncInProgress ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <AutorenewRoundedIcon sx={{ fontSize: 18 }} />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}

            <Dialog
                open={resolvedDetailsOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {dialogTitle}
                        </Typography>
                        {headerControl ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
                                {headerControl}
                            </Box>
                        ) : null}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {syncError ? <Alert severity="error" sx={{ mb: 1.5 }}>{syncError}</Alert> : null}
                    {dialogHasVisibleSummary ? (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                                <Chip
                                    size="small"
                                    color={summary.loading ? 'info' : summary.statusColor}
                                    label={summary.loading ? 'Checking...' : summary.freshnessLabel}
                                />
                                {summary.loading ? <CircularProgress size={18} /> : null}
                            </Box>

                            {optimizerPresentation && !summary.loading && showDetailText && dialogSummary?.detailText ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {dialogSummary.detailText}
                                </Typography>
                            ) : null}

                            {dialogImportRows.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                        Importing to Position Sizing
                                    </Typography>
                                    <SecurityListTable
                                        rows={dialogImportRows}
                                        gmvField="import_gmv"
                                        ariaLabel="Securities importing to Position Sizing"
                                        identityDisplayField={identityDisplayField}
                                        onIdentityDisplayFieldChange={setIdentityDisplayField}
                                    />
                                </Box>
                            ) : null}

                            {dialogRemovalRows.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                        Removing from Position Sizing
                                    </Typography>
                                    <SecurityListTable
                                        rows={dialogRemovalRows}
                                        gmvField="removal_gmv"
                                        ariaLabel="Securities removing from Position Sizing"
                                        identityDisplayField={identityDisplayField}
                                        onIdentityDisplayFieldChange={setIdentityDisplayField}
                                    />
                                </Box>
                            ) : null}

                            {dialogDraftOnlyRows.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                                        Securities kept only in Position Sizing
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                        These securities are in your draft but not your Actual Portfolio. Sync Draft keeps them; they do not make the draft out of date.
                                    </Typography>
                                    <SecurityListTable
                                        rows={dialogDraftOnlyRows}
                                        gmvField="draft_gmv"
                                        ariaLabel="Draft-only Position Sizing additions"
                                        identityDisplayField={identityDisplayField}
                                        onIdentityDisplayFieldChange={setIdentityDisplayField}
                                    />
                                </Box>
                            ) : null}

                            {contextComparisonRows.length > 0 ? (
                                <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                                            Context Comparison
                                        </Typography>
                                        <ContextComparisonTable
                                            rows={contextComparisonRows}
                                            showIncoming={showIncomingContext}
                                            loading={Boolean(summary.loading)}
                                        />
                                </Box>
                            ) : (summary.loading || dialogSummary?.contextState === 'unavailable') ? (
                                <Box sx={{ minHeight: 96, display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {summary.loading
                                            ? `Checking ${optimizerPresentation ? 'Position Sizing context' : 'draft freshness'}…`
                                            : (!optimizerPresentation && dialogSummary?.detailText)
                                                || 'Position Sizing context details are unavailable. Sync Draft to continue.'}
                                    </Typography>
                                </Box>
                            ) : null}

                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No saved position-sizing draft is available yet. Use the import controls above to upload one.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {dialogShowSyncAction ? (
                        <Button
                            variant="contained"
                            onClick={handleSync}
                            disabled={isSyncActionDisabled || summary.loading}
                            startIcon={syncInProgress ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {syncInProgress ? 'Syncing...' : 'Sync Draft'}
                        </Button>
                    ) : null}
                    <Button onClick={handleCloseDetails}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

DraftFreshnessHeaderControl.propTypes = {
    summary: PropTypes.shape({
        visible: PropTypes.bool,
        loading: PropTypes.bool,
        isStale: PropTypes.bool,
        statusColor: PropTypes.string,
        freshnessLabel: PropTypes.string,
        detailText: PropTypes.string,
        contextState: PropTypes.oneOf([
            'checking',
            'current',
            'stale',
            'unknown',
            'unavailable',
        ]),
        syncActionVisible: PropTypes.bool,
        contextComparisonRows: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            savedValue: PropTypes.string.isRequired,
            currentValue: PropTypes.string.isRequired,
            matches: PropTypes.bool,
        })),
        importRows: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            cognive_id: PropTypes.string.isRequired,
            ticker: PropTypes.string,
            company_name: PropTypes.string,
            country: PropTypes.string,
            import_gmv: PropTypes.number,
        })),
        removalRows: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            cognive_id: PropTypes.string.isRequired,
            ticker: PropTypes.string,
            company_name: PropTypes.string,
            country: PropTypes.string,
            removal_gmv: PropTypes.number,
        })),
        draftOnlyRows: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string.isRequired,
            cognive_id: PropTypes.string.isRequired,
            ticker: PropTypes.string,
            company_name: PropTypes.string,
            country: PropTypes.string,
            draft_gmv: PropTypes.number,
        })),
    }),
    onSyncClick: PropTypes.func,
    syncDisabled: PropTypes.bool,
    syncInProgress: PropTypes.bool,
    detailsOpen: PropTypes.bool,
    onDetailsOpenChange: PropTypes.func,
    triggerVariant: PropTypes.oneOf(['status', 'imports', 'dialog-only']),
    headerControl: PropTypes.node,
    presentation: PropTypes.oneOf(['draft', 'optimizer']),
};

export default DraftFreshnessHeaderControl;
